<!--
  The add / edit dialog for a board column (Prospect and Pipeline Board): a name and filter rows. Each row
  is field → condition → value; the value input follows the field (pick list, date, days, number, text).
  The parent passes the fields the server offers and the conditions per field kind, and gets back
  { id, title, filters } on save. The server validates everything again; this only keeps input tidy.
-->
<template>
  <div class="cx bce-veil" role="presentation" @mousedown.self="$emit('cancel')">
    <section class="cx-card bce" role="dialog" aria-modal="true" :aria-label="isNew ? 'Add a column' : 'Edit column'">
      <header class="bce-head">
        <h2 class="cx-h2">{{ isNew ? 'Add a column' : 'Edit column' }}</h2>
        <button type="button" class="bce-x" aria-label="Close" @click="$emit('cancel')">×</button>
      </header>

      <label class="bce-label">Column name
        <input ref="title" v-model="draft.title" class="cx-select bce-input" maxlength="60" placeholder="e.g. Closing in the next 2 weeks" />
      </label>

      <div class="bce-label">Show {{ noun }} where</div>
      <p v-if="!draft.filters.length" class="cx-small cx-muted" style="margin: 0;">No filters yet: this column would show every {{ singular }} you can see.</p>
      <div v-for="(f, i) in draft.filters" :key="i" class="bce-row">
        <span class="bce-and cx-small cx-muted">{{ i === 0 ? 'Where' : 'and' }}</span>
        <select v-model="f.field" class="cx-select" :aria-label="`Filter ${i + 1} field`" @change="onField(f)">
          <option value="" disabled>Pick a field</option>
          <optgroup v-for="g in groups" :key="g.name" :label="g.name">
            <option v-for="fd in g.items" :key="fd.key" :value="fd.key">{{ fd.label }}</option>
          </optgroup>
        </select>
        <select v-model="f.op" class="cx-select" :aria-label="`Filter ${i + 1} condition`" :disabled="!f.field">
          <option v-for="o in opsFor(f.field)" :key="o[0]" :value="o[0]">{{ o[1] }}</option>
        </select>
        <template v-if="f.field">
          <select v-if="inputFor(f) === 'choice'" v-model="f.value" class="cx-select" :aria-label="`Filter ${i + 1} value`">
            <option value="" disabled>Choose</option>
            <option v-for="c in choicesFor(f.field)" :key="c" :value="c">{{ c }}</option>
          </select>
          <input v-else-if="inputFor(f) === 'date'" v-model="f.value" type="date" class="cx-select" :aria-label="`Filter ${i + 1} date`" />
          <span v-else-if="inputFor(f) === 'days'" class="bce-days">
            <input v-model="f.value" type="number" min="0" max="3650" class="cx-select" :aria-label="`Filter ${i + 1} days`" /> days
          </span>
          <input v-else-if="inputFor(f) === 'number'" v-model="f.value" type="number" step="any" class="cx-select" :aria-label="`Filter ${i + 1} number`" />
          <input v-else-if="inputFor(f) === 'text'" v-model="f.value" class="cx-select" maxlength="200" :list="choicesFor(f.field).length ? `bce-dl-${i}` : null" :aria-label="`Filter ${i + 1} value`" />
          <datalist v-if="inputFor(f) === 'text' && choicesFor(f.field).length" :id="`bce-dl-${i}`">
            <option v-for="c in choicesFor(f.field)" :key="c" :value="c"></option>
          </datalist>
        </template>
        <button type="button" class="bce-x" :aria-label="`Remove filter ${i + 1}`" @click="draft.filters.splice(i, 1)">×</button>
      </div>
      <button type="button" class="cx-btn cx-btn--sm" style="align-self: flex-start;" :disabled="draft.filters.length >= 12" @click="addRow">+ Add a filter</button>

      <div v-if="problem" class="cx-banner cx-banner--error" role="alert">{{ problem }}</div>

      <footer class="bce-foot">
        <button v-if="!isNew" type="button" class="cx-btn cx-btn--sm bce-danger" @click="$emit('remove', draft.id)">Remove column</button>
        <span style="flex: 1;"></span>
        <button type="button" class="cx-btn cx-btn--sm" @click="$emit('cancel')">Cancel</button>
        <button type="button" class="cx-btn cx-btn--sm cx-btn--primary" @click="save">{{ isNew ? 'Add column' : 'Save' }}</button>
      </footer>
    </section>
  </div>
</template>

<script>
import '@/assets/css/connect.css'
import { cleanColumn, newColumnId, valueInputFor, NO_VALUE_OPS } from '@/helpers/connect/boardColumns.js'

export default {
  name: 'BoardColumnEditor',
  props: {
    column: { type: Object, default: null }, // null = a new column
    fields: { type: Array, required: true }, // [{ key, label, kind, group?, choices? }]
    opsByKind: { type: Object, required: true }, // { kind: [[value, label], …] }
    noun: { type: String, default: 'contacts' },
    singular: { type: String, default: 'contact' },
    // Extra choices for a field when the server doesn't send any (e.g. contact statuses / types).
    extraChoices: { type: Object, default: () => ({}) }
  },
  // 'field': a filter row now uses this field, so the parent can fetch its pick list if it loads them lazily.
  emits: ['save', 'cancel', 'remove', 'field'],
  data() {
    const base = this.column ? cleanColumn(this.column) : { id: newColumnId(), title: '', filters: [] }
    return { draft: { ...base, filters: base.filters.map(f => ({ ...f })) }, problem: '' }
  },
  computed: {
    isNew() { return !this.column },
    groups() {
      const out = []
      this.fields.forEach(f => {
        const name = f.group || 'Fields'
        let g = out.find(x => x.name === name)
        if (!g) {
          g = { name, items: [] }
          out.push(g)
        }
        g.items.push(f)
      })
      return out
    }
  },
  mounted() {
    if (this.isNew) this.addRow()
    this.draft.filters.forEach(f => { if (f.field) this.$emit('field', f.field) })
    this.$nextTick(() => this.$refs.title?.focus())
    this.onKey = e => { if (e.key === 'Escape') this.$emit('cancel') }
    window.addEventListener('keydown', this.onKey)
  },
  beforeUnmount() { window.removeEventListener('keydown', this.onKey) },
  methods: {
    def(key) { return this.fields.find(f => f.key === key) },
    opsFor(key) {
      const d = this.def(key)
      return d ? (this.opsByKind[d.kind] || this.opsByKind.text || []) : []
    },
    choicesFor(key) {
      const d = this.def(key)
      return (d && d.choices && d.choices.length ? d.choices : this.extraChoices[key]) || []
    },
    inputFor(f) {
      const d = this.def(f.field)
      return d ? valueInputFor(d.kind, f.op, this.choicesFor(f.field).length > 0) : 'none'
    },
    addRow() { this.draft.filters.push({ field: '', op: '', value: '' }) },
    onField(f) {
      this.$emit('field', f.field)
      const ops = this.opsFor(f.field)
      f.op = ops.length ? ops[0][0] : ''
      f.value = f.op === 'in_next_days' || f.op === 'in_last_days' ? '14' : ''
    },
    save() {
      this.problem = ''
      const filters = this.draft.filters.filter(f => f.field)
      for (const f of filters) {
        const d = this.def(f.field)
        if (!f.op) { this.problem = `Pick a condition for ${d ? d.label : f.field}.`; return }
        if (!NO_VALUE_OPS.includes(f.op) && String(f.value ?? '').trim() === '') { this.problem = `Enter a value for ${d ? d.label : f.field}.`; return }
        if ((f.op === 'in_next_days' || f.op === 'in_last_days') && !(Number(f.value) >= 0 && Number(f.value) <= 3650)) {
          this.problem = 'Days must be between 0 and 3650.'; return
        }
      }
      const title = String(this.draft.title || '').trim() || (filters[0] && filters[0].value) || 'Untitled column'
      this.$emit('save', cleanColumn({ id: this.draft.id, title, filters }))
    }
  }
}
</script>

<style scoped>
.bce-veil { position: fixed; inset: 0; z-index: 1000; background: rgba(15, 23, 42, .35); display: flex; align-items: flex-start; justify-content: center; padding: 8vh 16px 16px; overflow: auto; }
.bce { width: min(760px, 100%); padding: 18px 20px; display: flex; flex-direction: column; gap: 12px; box-shadow: 0 20px 40px rgba(16, 24, 40, .18); }
.bce-head { display: flex; align-items: center; justify-content: space-between; }
.bce-label { display: flex; flex-direction: column; gap: 6px; font-size: 12px; font-weight: 600; color: #6b7280; }
.bce-input { height: 38px; font-size: 14px; }
.bce-row { display: grid; grid-template-columns: 48px minmax(0, 1.3fr) minmax(0, 1fr) minmax(0, 1.2fr) 28px; gap: 8px; align-items: center; }
.bce-row .cx-select { width: 100%; height: 36px; }
.bce-and { text-align: right; }
.bce-days { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; color: #6b7280; }
.bce-days .cx-select { width: 90px; }
.bce-x { border: 0; background: transparent; color: #6b7280; font-size: 18px; line-height: 1; width: 28px; height: 28px; border-radius: 6px; cursor: pointer; }
.bce-x:hover { background: #eef0f3; color: #a8261b; }
.bce-foot { display: flex; align-items: center; gap: 8px; border-top: 1px solid #eef0f3; padding-top: 12px; }
.bce-danger { color: #a8261b; border-color: #f6cfcb; }
@media (max-width: 640px) {
  .bce-row { grid-template-columns: minmax(0, 1fr) 28px; }
  .bce-and { display: none; }
  .bce-row > select, .bce-row > input, .bce-row > .bce-days { grid-column: 1; }
}
</style>
